import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Input, Select, Table, Tag, Tooltip, message } from "antd";
import { useNavigate } from "react-router-dom";
import { Play, Search } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import {
  anTableProps,
  FilterCard,
  FilterField,
  PageHeader,
  PageShell,
  StatusPill,
  TableCard,
  tableEmpty,
} from "../../../design-system/tableUi";
import { useDebounce } from "../../../common/debounce";
import { listJumpServer, sshAccessLog } from "../../../service/sshProxyService";
import { card, CopyButton, Note, PanelHead } from "../ssh-proxy/sshProxyUi";
import ActivityChart, { OUTCOME_COLORS } from "./ActivityChart";

// PAM → Auth Logs: the SSH access log. One row per access attempt through a
// gateway — who, which account on which machine, the outcome and why, how long,
// and the recording. Counts and the chart cover the whole window under the
// filters (counts ignore the outcome filter); the table pages with a cursor.

const PAGE_SIZE = 50;
const MIN = 60 * 1000;
const HOUR = 60 * MIN;

// Window presets, each with a chart bucket that gives a readable bar count.
const RANGES = [
  { value: "1h", label: "Last hour", ms: HOUR, bucketMs: 5 * MIN },
  { value: "24h", label: "Last 24 hours", ms: 24 * HOUR, bucketMs: 15 * MIN },
  { value: "7d", label: "Last 7 days", ms: 7 * 24 * HOUR, bucketMs: 2 * HOUR },
  { value: "30d", label: "Last 30 days", ms: 30 * 24 * HOUR, bucketMs: 12 * HOUR },
];

const OUTCOMES = {
  approved: { label: "Approved", tone: "success" },
  denied: { label: "Denied", tone: "danger" },
  refused: { label: "Refused", tone: "danger", hint: "Stopped at the gateway, before any push." },
  no_answer: { label: "No answer", tone: "warn" },
  failed: { label: "Failed", tone: "warn", hint: "Approved, but the target was unreachable." },
};

const OUTCOME_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "denied", label: "Denied" },
  { value: "refused", label: "Refused" },
  { value: "no_answer", label: "No answer" },
  { value: "failed", label: "Failed" },
  { value: "live", label: "Live now" },
];

const fmtTime = (t) =>
  t
    ? new Date(t).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })
    : "—";

const ONE_LINE = { display: "block", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const fmtDuration = (sec) => {
  if (sec == null) return null;
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${r}s`;
  return `${r}s`;
};

const targetLabel = (t = {}) => {
  const host = t.hostname || t.host || "";
  if (!t.account && !host) return "—";
  const account = isTemporary(t) && t.requested ? `${t.requested} → ${t.account}` : t.account;
  return account ? `${account}@${host || "?"}` : host;
};

// A temporary user: the operator asked for "temp" (target.requested) and the
// session got an account of its own (target.account, e.g. an-sathyam-2997).
const isTemporary = (t = {}) => t.credential === "temporary";

const TempBadge = () => (
  <Tooltip title="An account created for this session alone, deleted when it ended.">
    <Tag color="purple" style={{ marginInlineEnd: 0, fontSize: 11 }}>
      Temporary user
    </Tag>
  </Tooltip>
);

const errorText = (err) =>
  err?.response?.status === 403
    ? "Administrator role required."
    : err?.response?.data?.error || "Couldn't load the SSH access log.";

function OutcomeCell({ row }) {
  const o = OUTCOMES[row.outcome] || { label: row.outcome || "Unknown", tone: "neutral" };
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <Tooltip title={o.hint}>
        <span style={{ width: "fit-content" }}>
          <StatusPill status={row.outcome} label={o.label} tone={o.tone} />
        </span>
      </Tooltip>
      {row.reasonLabel && <span style={{ fontSize: 11.5, color: AN_COLORS.muted }}>{row.reasonLabel}</span>}
    </div>
  );
}

// The number stays in ink; a small swatch ties it to the chart's colour.
function Stat({ label, value, color }) {
  return (
    <div style={{ ...card, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, fontFamily: AN_MONO, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", color: AN_COLORS.muted }}>
        {color && <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />}
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, color: AN_COLORS.ink, marginTop: 4 }}>{value ?? "—"}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "110px 1fr auto", gap: 10, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${AN_COLORS.line2}` }}>
      <span style={{ fontSize: 12, color: AN_COLORS.muted }}>{label}</span>
      <span style={{ fontFamily: AN_MONO, fontSize: 12, color: value ? AN_COLORS.ink : AN_COLORS.muted, wordBreak: "break-all" }}>{value || "—"}</span>
      {value ? <CopyButton text={value} /> : <span />}
    </div>
  );
}

export default function SshAccessLog() {
  const navigate = useNavigate();
  const [messageApi, msgCtx] = message.useMessage();

  const [operator, setOperator] = useState("");
  const [target, setTarget] = useState("");
  const debouncedOperator = useDebounce(operator, 400);
  const debouncedTarget = useDebounce(target, 400);
  const [gatewayId, setGatewayId] = useState(undefined);
  const [outcome, setOutcome] = useState(undefined);
  const [rangeKey, setRangeKey] = useState("24h");

  const [gateways, setGateways] = useState([]);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ counts: null, timeline: [], bucketMs: 0 });
  const [nextCursor, setNextCursor] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    listJumpServer({
      payload: { pageSize: 200 },
      onSuccess: (data) => setGateways(data?.jumpServers || []),
      onFailure: () => setGateways([]),
    });
  }, []);

  // The window is fixed when the filters change, so "Load more" pages the same
  // window rather than one that has moved on.
  const query = useMemo(() => {
    const range = RANGES.find((r) => r.value === rangeKey) || RANGES[1];
    const to = new Date();
    return {
      from: new Date(to.getTime() - range.ms).toISOString(),
      to: to.toISOString(),
      bucketMs: range.bucketMs,
      limit: PAGE_SIZE,
      ...(debouncedOperator.trim() ? { operator: debouncedOperator.trim() } : {}),
      ...(debouncedTarget.trim() ? { target: debouncedTarget.trim() } : {}),
      ...(gatewayId ? { gatewayId: Number(gatewayId) } : {}),
      ...(outcome ? { outcome } : {}),
    };
  }, [rangeKey, debouncedOperator, debouncedTarget, gatewayId, outcome]);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    sshAccessLog({
      payload: query,
      onSuccess: (data) => {
        setLoading(false);
        setRows(data.rows || []);
        setSummary({ counts: data.counts || null, timeline: data.timeline || [], bucketMs: data.bucketMs || query.bucketMs });
        setNextCursor(data.nextCursor || "");
      },
      onFailure: (err) => {
        setLoading(false);
        setRows([]);
        setSummary({ counts: null, timeline: [], bucketMs: 0 });
        setNextCursor("");
        setError(errorText(err));
      },
    });
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = () => {
    setLoadingMore(true);
    sshAccessLog({
      payload: { ...query, cursor: nextCursor },
      onSuccess: (data) => {
        setLoadingMore(false);
        setRows((r) => [...r, ...(data.rows || [])]);
        setNextCursor(data.nextCursor || "");
      },
      onFailure: (err) => {
        setLoadingMore(false);
        messageApi.error(errorText(err));
      },
    });
  };

  const play = (id) => navigate(`/pam/jumpServer-sessionRecordings/play/${encodeURIComponent(id)}`);

  const gatewayOptions = gateways.map((g) => ({ value: String(g.id), label: g.serverName || `Gateway #${g.id}` }));
  const c = summary.counts;

  const applied = [
    debouncedOperator && { key: "op", label: `Operator: ${debouncedOperator}`, clear: () => setOperator("") },
    debouncedTarget && { key: "tg", label: `Target: ${debouncedTarget}`, clear: () => setTarget("") },
    gatewayId && { key: "gw", label: `Gateway: ${gatewayOptions.find((g) => g.value === gatewayId)?.label || gatewayId}`, clear: () => setGatewayId(undefined) },
    outcome && { key: "oc", label: `Outcome: ${OUTCOME_OPTIONS.find((o) => o.value === outcome)?.label}`, clear: () => setOutcome(undefined) },
  ].filter(Boolean);

  const columns = [
    { title: "Time", key: "time", width: "8.75rem", render: (_, r) => <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{fmtTime(r.time)}</span> },
    {
      title: "Operator",
      key: "operator",
      render: (_, r) =>
        r.operator?.email ? (
          <Tooltip title={r.operator.email}>
            <span style={{ ...ONE_LINE, fontWeight: 500, color: AN_COLORS.ink }}>{r.operator.email}</span>
          </Tooltip>
        ) : (
          <span style={{ display: "grid", minWidth: 0 }}>
            <span style={{ color: AN_COLORS.ink2 }}>Unknown key</span>
            {r.operator?.keyFingerprint && (
              // Shortened: the whole fingerprint is in the tooltip and the drawer.
              <Tooltip title={r.operator.keyFingerprint}>
                <span style={{ ...ONE_LINE, fontFamily: AN_MONO, fontSize: 11, color: AN_COLORS.muted }}>{r.operator.keyFingerprint}</span>
              </Tooltip>
            )}
          </span>
        ),
    },
    {
      title: "Target",
      key: "target",
      render: (_, r) => (
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
          <span style={{ fontFamily: AN_MONO, fontSize: 12, overflowWrap: "anywhere" }}>{targetLabel(r.target)}</span>
          {isTemporary(r.target) && <TempBadge />}
        </span>
      ),
    },
    {
      title: "Gateway",
      key: "gateway",
      width: "8.125rem",
      render: (_, r) => <span style={{ overflowWrap: "anywhere" }}>{r.gateway?.name || (r.gateway?.id ? `Gateway #${r.gateway.id}` : "—")}</span>,
    },
    { title: "Outcome", key: "outcome", width: "12.5rem", render: (_, r) => <OutcomeCell row={r} /> },
    {
      // Length and the recording's play button share a column, so the table
      // fits without scrolling sideways.
      title: "Length",
      key: "length",
      width: "7.5rem",
      render: (_, r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {r.live ? (
            <StatusPill status="live" label="Live" tone="info" />
          ) : (
            <span style={{ fontFamily: AN_MONO, fontSize: 12, color: r.durationSec == null ? AN_COLORS.muted : AN_COLORS.ink }}>
              {fmtDuration(r.durationSec) || "—"}
            </span>
          )}
          {r.recordings?.length ? (
          <Tooltip title="Play the recording">
            <Button
              size="small"
              type="text"
              aria-label="Play the recording"
              icon={<Play size={14} color={AN_COLORS.primary} />}
              onClick={(e) => {
                e.stopPropagation();
                play(r.recordings[0].id);
              }}
            />
          </Tooltip>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <PageShell>
      {msgCtx}
      <PageHeader
        eyebrow="PAM · Logs"
        title="SSH Access Log"
        subtitle="Every SSH access attempt through a gateway: who, to which account on which machine, the outcome and why."
      />

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Note tone="danger">{error}</Note>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 14 }}>
        <Stat label="Attempts" value={c?.attempts} />
        <Stat label="Approved" value={c?.approved} color={OUTCOME_COLORS.approved} />
        <Stat label="Denied or refused" value={c ? (c.denied || 0) + (c.refused || 0) : null} color={OUTCOME_COLORS.denied} />
        <Stat label="Live now" value={c?.live} />
      </div>
      {c?.partial && (
        <div style={{ marginBottom: 14 }}>
          <Note tone="warn">This window holds more attempts than could be counted — the totals and the chart are a lower bound. Narrow the time range for exact numbers.</Note>
        </div>
      )}

      <ActivityChart timeline={summary.timeline} bucketMs={summary.bucketMs} loading={loading} />

      <FilterCard
        columns="1.2fr 1.2fr 1fr 1fr 1fr"
        applied={applied}
        onClearAll={() => {
          setOperator("");
          setTarget("");
          setGatewayId(undefined);
          setOutcome(undefined);
        }}
        showing={rows.length}
        total={c?.attempts}
      >
        <FilterField label="Operator">
          <Input
            allowClear
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            placeholder="name@company.com"
            style={{ height: 36 }}
          />
        </FilterField>
        <FilterField label="Target">
          <Input
            allowClear
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            placeholder="Account, host or endpoint"
            style={{ height: 36 }}
          />
        </FilterField>
        <FilterField label="Gateway">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Any gateway"
            value={gatewayId}
            onChange={setGatewayId}
            options={gatewayOptions}
            style={{ width: "100%", height: 36 }}
          />
        </FilterField>
        <FilterField label="Outcome">
          <Select allowClear placeholder="Any outcome" value={outcome} onChange={setOutcome} options={OUTCOME_OPTIONS} style={{ width: "100%", height: 36 }} />
        </FilterField>
        <FilterField label="Time range">
          <Select value={rangeKey} onChange={setRangeKey} options={RANGES.map(({ value, label }) => ({ value, label }))} style={{ width: "100%", height: 36 }} />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={rows}
          rowKey={(r, i) => r.sessionId || `${r.time}-${i}`}
          loading={loading}
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: "55rem" }}
          onRow={(r) => ({ onClick: () => setSelected(r), style: { cursor: "pointer" } })}
          locale={tableEmpty(loading, applied.length ? "No attempts match your filters." : "No SSH access attempts in this window.")}
        />
        {nextCursor && (
          <div style={{ display: "flex", justifyContent: "center", padding: 12, borderTop: `1px solid ${AN_COLORS.line2}` }}>
            <Button loading={loadingMore} onClick={loadMore}>
              Load more
            </Button>
          </div>
        )}
      </TableCard>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        width={520}
        title={selected ? `${selected.operator?.email || "Unknown key"} → ${targetLabel(selected.target)}` : ""}
      >
        {selected && (
          <div style={{ display: "grid", gap: 18, fontFamily: AN_FONT }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <OutcomeCell row={selected} />
              {selected.live && <StatusPill status="live" label="Live" tone="info" />}
              {isTemporary(selected.target) && <TempBadge />}
              {!selected.live && fmtDuration(selected.durationSec) && (
                <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>Length {fmtDuration(selected.durationSec)}</span>
              )}
            </div>

            <div>
              <PanelHead title="Timeline" />
              {(selected.timeline || []).length === 0 ? (
                <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>No steps recorded.</span>
              ) : (
                <div style={{ display: "grid", gap: 0 }}>
                  {selected.timeline.map((s, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 10, padding: "7px 0", borderBottom: `1px solid ${AN_COLORS.line2}` }}>
                      <span style={{ fontFamily: AN_MONO, fontSize: 11.5, color: AN_COLORS.muted }}>{fmtTime(s.time)}</span>
                      <span style={{ fontSize: 12.5, color: AN_COLORS.ink }}>{s.label || s.event}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <PanelHead title="Details" />
              <DetailRow label="Source IP" value={selected.operator?.srcIp} />
              <DetailRow label="Key" value={selected.operator?.keyFingerprint} />
              <DetailRow label="Policy" value={selected.policy?.name || selected.policy?.id} />
              <DetailRow label="Session" value={selected.sessionId} />
              <DetailRow label="Decision" value={selected.decisionId} />
            </div>

            <div>
              <PanelHead title="Recordings" />
              {(selected.recordings || []).length === 0 ? (
                <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>No recording for this attempt.</span>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {selected.recordings.map((rec, i) => (
                    <div key={rec.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>
                        {selected.recordings.length > 1 ? `Channel ${i + 1}` : "Session recording"}
                        {rec.sizeBytes ? <span style={{ color: AN_COLORS.muted }}> · {(rec.sizeBytes / 1024).toFixed(1)} KB</span> : null}
                      </span>
                      <Button size="small" icon={<Play size={12} style={{ verticalAlign: -1, marginRight: 4 }} />} onClick={() => play(rec.id)}>
                        Play
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </PageShell>
  );
}
